import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import useRefetchOnFocus from "@/hooks/useRefetchOnFocus";
import LoaderOlimpo from "@/components/LoaderOlimpo";
import CountUp from "@/components/CountUp";
import { useAuth } from "@/context/AuthContext";
import { consumeOAuthRedirect } from "@/lib/oauthRedirect";
import Tour from "@/components/Tour";
import ResumenAndando from "@/components/ResumenAndando";
import ChequeoVida from "@/components/ChequeoVida";
import Encuesta from "@/components/Encuesta";
import { useMomentos } from "@/context/MomentosContext";
import { toast } from "sonner";

const TOUR_MESA = [
  { selector: '[data-testid="tarjeta-hoy"]', texto: "Esto es lo único que tenés que hacer hoy. Cuando lo termines, acá aparece lo que sigue." },
  { selector: '[data-testid="mesa-contadores"]', texto: "Acá vas a ver las horas que te vas ahorrando. Hoy están en cero." },
  { selector: '[data-testid="nav-principal"]', texto: "Tus procesos y el contexto de tu empresa quedan siempre acá arriba." },
];

function saludoHora() {
  const h = new Date().getHours();
  if (h < 12) return "Buen día";
  if (h < 20) return "Buenas tardes";
  return "Buenas noches";
}

export default function Mesa() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [encuesta, setEncuesta] = useState(null);
  const { pedir } = useMomentos();

  const esEquipo = user?.rol === "admin" || user?.rol === "closer" || user?.email === "equipo@olimpo.com";

  // Red de seguridad: si una cuenta llegó a la mesa sin ver el video de bienvenida (cuentas viejas
  // que entraron por una ruta directa), se lo mostramos una sola vez. El sessionStorage evita el loop
  // mientras el estado del usuario se refresca.
  useEffect(() => {
    if (esEquipo || !user) return;
    if (!user.bienvenida_vista && !sessionStorage.getItem("olimpo:bienvenida_redir")) {
      sessionStorage.setItem("olimpo:bienvenida_redir", "1");
      nav("/bienvenida-video", { replace: true });
      return;
    }
    // Punto FINAL de la cadena obligatoria: si el cliente venía del consentimiento OAuth, vuelve ahí.
    if (user.bienvenida_vista) {
      const back = consumeOAuthRedirect();
      if (back) nav(back, { replace: true });
    }
  }, [user, esEquipo, nav]);

  const cargarMesa = useCallback(async () => {
    if (esEquipo) return;
    try { const res = await api.get("/mesa"); setData(res.data); } catch (e) { /* noop */ }
  }, [esEquipo]);

  useEffect(() => {
    if (esEquipo) return;
    (async () => {
      const res = await api.get("/mesa");
      setData(res.data);
      // La encuesta se dispara acá, al entrar a la Mesa después del logro (nunca encima del logro).
      try { const p = await api.get("/encuesta/pendiente"); if (p.data?.pendiente) setEncuesta(p.data); } catch {}
    })();
  }, [esEquipo]);

  // Al volver a la pestaña, releer la mesa (contadores, tarjeta de hoy, estado). No re-dispara la encuesta.
  useRefetchOnFocus(cargarMesa, !esEquipo);

  // Momento 6: si llega a tener la cola vacía (todo andando) antes de un 2do proceso, acá.
  useEffect(() => {
    if (data && data.modo === "todo_andando") {
      const h = Math.round(data?.contadores?.horas_recuperadas || 0);
      pedir("m6", { horas: h, dias: Math.max(1, Math.round(h / 8)) });
    }
  }, [data, pedir]);

  const responderSeguimiento = async (pudo) => {
    setData((d) => ({ ...d, seguimiento_tiempo: null }));
    try { await api.post("/momentos/seguimiento-tiempo", { pudo }); } catch {}
    if (!pudo) toast("Dale, arranquemos con eso. Contame en el chat qué necesitás para empezar.", { duration: 6000 });
  };

  const chequeoVidaListo = async () => {
    setData((d) => ({ ...d, chequeo_vida: null }));
    try { const res = await api.get("/mesa"); setData(res.data); } catch {}
  };

  if (esEquipo) return <Navigate to="/admin" replace />;

  if (!data) return <LoaderOlimpo />;

  const c = data.contadores;
  const t = data.tarjeta;
  const todoAndando = data.modo === "todo_andando" && data.resumen;

  const seguir = () => {
    if (t?.accion === "elegir") nav("/procesos");
    else if (t?.ruta) nav(t.ruta);
  };

  return (
    <div className="max-w-[900px] mx-auto w-full px-6 md:px-8 pt-[13vh] pb-24">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="titulo mb-3" style={{ fontSize: "clamp(32px, 4.5vw, 42px)", lineHeight: 1.05 }}>
          {saludoHora()}, {data.nombre}.
        </h1>
        <p className="text-gris text-sm mb-12" data-testid="mesa-estado">
          {data.estado_txt}{data.estado_nombre && <span className="text-dorado"> {data.estado_nombre}</span>}
        </p>
      </motion.div>

      {data.chequeo_vida && (
        <ChequeoVida chequeo={data.chequeo_vida} onListo={chequeoVidaListo} />
      )}

      {data.seguimiento_tiempo && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl p-5 mb-10" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }} data-testid="seguimiento-tiempo">
          <p className="font-serif" style={{ fontSize: 22, color: "#EDEDED", lineHeight: 1.4 }}>
            Hace dos semanas dijiste que ibas a usar esas horas para <span style={{ color: "#C9A84C" }}>{data.seguimiento_tiempo.texto}</span>. ¿Pudiste?
          </p>
          <div className="flex gap-3 mt-4">
            <button onClick={() => responderSeguimiento(true)} className="font-sans rounded-full px-6 py-2.5" style={{ background: "#C9A84C", color: "#0A0A0A", fontSize: 14, fontWeight: 600 }} data-testid="seguimiento-si">Sí</button>
            <button onClick={() => responderSeguimiento(false)} className="font-sans rounded-full px-6 py-2.5" style={{ background: "transparent", border: "1px solid #2A2A2A", color: "#D8D8D8", fontSize: 14 }} data-testid="seguimiento-no">Todavía no</button>
          </div>
        </motion.div>
      )}

      {todoAndando ? (
        <ResumenAndando resumen={data.resumen} nombre={user?.empresa?.nombre} />
      ) : t && (
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          className="rounded-xl p-8 w-full"
          style={{ border: "1px solid rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.05)" }}
          data-testid="tarjeta-hoy"
        >
          <div className="mono-label text-dorado mb-4">{t.etiqueta}</div>
          <h2 className="titulo text-2xl mb-8">{t.titulo}</h2>
          {t.accion === "diagnostico" && (
            <p className="text-gris text-sm mb-8 -mt-4" data-testid="mesa-tomate-tiempo">
              Tomate los veinte enteros. Es lo que hace que todo lo que sigue sea tuyo y no un consejo genérico.
            </p>
          )}
          <button className="btn-dorado" onClick={seguir} data-testid="tarjeta-seguir">SEGUIR</button>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="mt-12 pt-8 border-t border-borde grid grid-cols-3 gap-4"
        data-testid="mesa-contadores"
      >
        <div>
          <div className="text-crema text-2xl font-mono mb-1" data-testid="contador-andando">{c.procesos_andando}</div>
          <div className="mono-label text-gris" style={{ fontSize: "10px" }}>PROCESOS ANDANDO</div>
        </div>
        <div>
          <div className="text-crema text-2xl font-mono mb-1" data-testid="contador-horas"><CountUp value={c.horas_recuperadas} /></div>
          <div className="mono-label text-gris" style={{ fontSize: "10px" }}>H / MES RECUPERADAS</div>
        </div>
        <div>
          <div className="text-crema text-2xl font-mono mb-1" data-testid="contador-cola">{c.en_cola}</div>
          <div className="mono-label text-gris" style={{ fontSize: "10px" }}>EN LA COLA</div>
        </div>
      </motion.div>

      {data.puertas_abiertas && (
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          onClick={() => nav("/operacion")}
          className="mt-6 w-full text-left rounded-xl p-5 flex items-center justify-between transition-colors hover:bg-dorado/[0.04]"
          style={{ border: "1px solid rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.04)" }}
          data-testid="mesa-puertas-acceso"
        >
          <div>
            <div className="mono-label text-dorado mb-1" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>LAS PUERTAS DEL OLIMPO</div>
            <div className="text-crema" style={{ fontSize: 15 }}>El mapa de tu operación, de quién dependés y tu sello.</div>
          </div>
          <span className="text-dorado text-xl">→</span>
        </motion.button>
      )}

      <Tour id="mesa" steps={TOUR_MESA} enabled={!!data && !todoAndando && !encuesta} />

      {encuesta && (
        <Encuesta
          procesoId={encuesta.proceso_id}
          pasos={encuesta.pasos || []}
          onDone={() => setEncuesta(null)}
          onSkip={() => setEncuesta(null)}
        />
      )}
    </div>
  );
}
